/**
 * The one place the panel talks to the network.
 *
 * Service modules call `api.get(...)` / `api.post(...)` with a path from
 * `endpoints.ts` and get back parsed JSON; everything shared — the base URL,
 * JSON headers, timeouts, turning an error response into a message a screen
 * can show — happens here rather than in each service.
 *
 * Error convention, matching what the screens branch on:
 *   - the server answered with a non-2xx  -> `ApiError`, carrying its message
 *   - anything else (offline, DNS, CORS,
 *     timeout, abort)                     -> the original error, untouched
 * So `errorText(e, "Network error…")` shows a real server message where there
 * is one and a plain fallback where there is not.
 */

import { ApiError } from "../http";
import {
  clearStoredSession,
  readStoredSession,
  writeStoredSession,
} from "../session";
import { API_BASE_URL, API_TIMEOUT_MS } from "./config";
import { API_ENDPOINTS } from "./endpoints";

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type QueryValue = string | number | boolean | null | undefined;

/** Query string values. `null`/`undefined` entries are dropped, so callers can
 *  pass optional filters straight through without pruning them first. */
export type QueryParams = Record<string, QueryValue>;

export interface RequestOptions {
  query?: QueryParams;
  /** Extra headers, merged over the defaults. */
  headers?: Record<string, string>;
  /** Lets a caller cancel — e.g. an effect aborting on unmount. */
  signal?: AbortSignal;
  /** Overrides `API_TIMEOUT_MS` for a single call. */
  timeoutMs?: number;
}

/** Message shown when the server said nothing useful about a failure. */
const STATUS_MESSAGES: Record<number, string> = {
  400: "The request could not be processed. Please check the details and try again.",
  401: "Your session has expired. Please sign in again.",
  403: "You do not have access to this.",
  404: "We could not find what you were looking for.",
  409: "That conflicts with something that already exists.",
  422: "Some of the details are not valid. Please review them and try again.",
  429: "Too many requests. Please wait a moment and try again.",
};

const buildUrl = (path: string, query?: QueryParams): string => {
  const url = new URL(`${API_BASE_URL}${path}`);
  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value));
    }
  });
  return url.toString();
};

/** Parses the body by content type. Returns `undefined` for an empty body —
 *  a 204, or a 500 that produced no output at all. */
async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;

  const text = await response.text();
  if (!text) return undefined;

  const isJson = (response.headers.get("content-type") ?? "").includes("json");
  if (!isJson) return text;

  try {
    return JSON.parse(text);
  } catch {
    // A JSON content type with a body that is not JSON — a proxy error page,
    // usually. The raw text is more useful to a developer than a parse error.
    return text;
  }
}

/**
 * Digs the human-readable part out of an error body: `{"detail": "…"}`,
 * `{"message": "…"}`, and field errors like `{"email": ["…"]}`, falling back
 * to a message for the status code.
 */
function errorMessage(body: unknown, status: number): string {
  if (typeof body === "string" && body.trim() && !body.trim().startsWith("<")) {
    return body.trim();
  }

  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;

    for (const key of ["detail", "message", "error"]) {
      const value = record[key];
      if (typeof value === "string" && value.trim()) return value.trim();
    }

    // First field error wins: screens show one message, and the first is the
    // one the user most likely needs to fix.
    for (const value of Object.values(record)) {
      if (Array.isArray(value) && typeof value[0] === "string" && value[0]) {
        return value[0];
      }
    }
  }

  return STATUS_MESSAGES[status] ?? `Request failed (${status}).`;
}

/**
 * How a successful response is read. `json` is everything the API answers in
 * words; `blob` is the handful of endpoints that answer with a file — an
 * invoice PDF — where the bytes are the response and parsing them would be
 * nonsense. A failure is read as JSON either way: an endpoint that serves a
 * file still reports why it could not in the usual envelope.
 */
type ResponseKind = "json" | "blob";

async function rawRequest<T>(
  method: Method,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  kind: ResponseKind = "json"
): Promise<T> {
  const { query, headers, signal, timeoutMs = API_TIMEOUT_MS } = options;

  // Our own controller so the timeout can abort the request, chained to the
  // caller's signal so their cancellation still gets through.
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const abortFromCaller = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener("abort", abortFromCaller);

  const accessToken = readStoredSession()?.access_token;

  try {
    const response = await fetch(buildUrl(path, query), {
      method,
      signal: controller.signal,
      headers: {
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      const payload = await readBody(response);
      throw new ApiError(
        errorMessage(payload, response.status),
        response.status,
        payload
      );
    }

    // Read inside the try, so a connection that drops midway through a large
    // file is still the timeout's to abort.
    if (kind !== "blob") return (await readBody(response)) as T;

    // An endpoint that answers with a file can still answer in words: a
    // framework falls back to its JSON renderer when it has nothing registered
    // for what was asked for, so a 200 carrying an error body is not a
    // document. It is read as the failure it is, message and all, rather than
    // handed on to be saved as a file that opens as nothing.
    if ((response.headers.get("content-type") ?? "").includes("json")) {
      const payload = await readBody(response);
      throw new ApiError(
        errorMessage(payload, response.status),
        response.status,
        payload
      );
    }

    return (await response.blob()) as T;
  } catch (error) {
    // A timeout is the one non-HTTP failure worth naming, because "network
    // error" sends the user to check a connection that is fine.
    if (timedOut) {
      throw new Error("The server took too long to respond.");
    }
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abortFromCaller);
  }
}

/** In flight while a refresh is under way, so two requests that 401 at once
 *  spend the refresh token once between them rather than one each. */
let refreshInFlight: Promise<string | null> | null = null;

/**
 * Trades the stored refresh token for a new pair. `null` where there was
 * nothing to trade, or the server would not take it — either way the caller
 * has nothing to retry with, and a refresh token the server has rejected is
 * cleared here rather than left to fail the same way again on the next call.
 *
 * Goes through `rawRequest` directly, not `request`: the retry wrapper below
 * would otherwise send a 401 from this very call back through this same
 * function.
 */
function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const session = readStoredSession();
      if (!session) return null;

      try {
        const tokens = await rawRequest<{
          access_token: string;
          refresh_token: string;
        }>("POST", API_ENDPOINTS.auth.refreshToken, {
          refresh_token: session.refresh_token,
        });

        writeStoredSession({ ...session, ...tokens });
        return tokens.access_token;
      } catch {
        clearStoredSession();
        return null;
      }
    })().finally(() => {
      refreshInFlight = null;
    });
  }

  return refreshInFlight;
}

/**
 * What every service actually calls. Same as `rawRequest`, except a 401 gets
 * one chance at a silent refresh-and-retry before it is shown to a screen —
 * an access token is short-lived by design, and the six-hour timer that used
 * to force a fresh sign-in is gone, so this is what keeps a session alive
 * across it rather than ending it at the first expired token. Signing out is
 * left to the person, at the button meant for it.
 */
async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
  kind: ResponseKind = "json"
): Promise<T> {
  try {
    return await rawRequest<T>(method, path, body, options, kind);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) return rawRequest<T>(method, path, body, options, kind);
    }
    throw error;
  }
}

/**
 * Verb helpers. `T` is the shape the endpoint returns; services declare it and
 * map the wire response to the panel's own types, so snake_case stops at the
 * service boundary.
 */
export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>("GET", path, undefined, options),

  /**
   * The same `GET`, for an endpoint that answers with a file rather than with
   * words — the bytes, undecoded, for the caller to hand to the browser.
   *
   * Callers name what they are asking for with an `Accept` header, since the
   * default one asks for JSON — but they should leave a wildcard on the end of
   * it. The API is DRF, which negotiates content against its registered
   * renderers before it authenticates, and a route that builds a file itself
   * has no renderer for that file's type: an `Accept` naming only
   * `application/pdf` comes back 406 without the view ever running. See
   * `downloadInvoice`, which spells the header out.
   */
  getBlob: (path: string, options?: RequestOptions) =>
    request<Blob>("GET", path, undefined, options, "blob"),

  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("POST", path, body, options),

  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>("PATCH", path, body, options),

  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>("DELETE", path, undefined, options),
};
